import { useRef, useState, useCallback } from 'react';
import { DEFAULT_ANNOTATION_STYLE, DEFAULT_TEXTBOX_STYLE } from '@/utils/cad-sheet';

export const DEFAULT_TOOL_STYLE = {
  strokeColor: '#000000',
  fontSize: 2.25,
  lineThickness: 0.5,
  opacity: 0.4,
  bold: true,
};

export function AnnotationCanvas({
  image, activeTool, selectedId, onSelect,
  onAddAnnotation, onUpdateAnnotation, onRemoveAnnotation,
  onAddTextBox, onUpdateTextBox, onRemoveTextBox,
  onAddHighlight, onRemoveHighlight,
  highlightColor, highlightOpacity, highlightStrokeWidth,
  activeToolStyle,
}) {
  const containerRef = useRef(null);
  const [rulerState, setRulerState] = useState('idle');
  const rulerStateRef = useRef('idle');
  const setRulerStateBoth = (s) => { rulerStateRef.current = s; setRulerState(s); };
  const [firstPoint, setFirstPoint] = useState({ x: 0, y: 0 });
  const [secondPoint, setSecondPoint] = useState({ x: 0, y: 0 });
  const [dimensionDragPoint, setDimensionDragPoint] = useState({ x: 0, y: 0 });
  const dimensionDragPointRef = useRef({ x: 0, y: 0 });
  const [pendingValue, setPendingValue] = useState('');
  const [showValueInput, setShowValueInput] = useState(false);
  const [frozenGeometry, setFrozenGeometry] = useState(null);
  const firstPointRef = useRef({ x: 0, y: 0 });
  const secondPointRef = useRef({ x: 0, y: 0 });
  const [textBoxPoint, setTextBoxPoint] = useState({ x: 0, y: 0 });
  const [showTextBoxInput, setShowTextBoxInput] = useState(false);
  const [textBoxValue, setTextBoxValue] = useState('');
  const [painting, setPainting] = useState(false);
  const [currentStroke, setCurrentStroke] = useState([]);
  const [dragging, setDragging] = useState(null);
  const dragStartRef = useRef(null);
  // Track if a drag just ended to suppress the spurious click
  const didDragRef = useRef(false);

  const getRelCoords = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    return { x: ((e.clientX - rect.left) / rect.width) * 100, y: ((e.clientY - rect.top) / rect.height) * 100 };
  };

  const getCursor = () => {
    switch (activeTool) {
      case 'ruler': return 'crosshair';
      case 'text': return 'text';
      case 'highlight': return 'crosshair';
      case 'delete': return 'pointer';
      default: return 'default';
    }
  };

  const computeDimensionGeometry = (p1, p2, dragPt) => {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const midX = (p1.x + p2.x) / 2;
    const midY = (p1.y + p2.y) / 2;
    const offsetX = dragPt.x - midX;
    const offsetY = dragPt.y - midY;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 0.5) return null;
    const nx = -dy / len;
    const ny = dx / len;
    const offsetDist = offsetX * nx + offsetY * ny;
    const ext1End = { x: p1.x + nx * offsetDist, y: p1.y + ny * offsetDist };
    const ext2End = { x: p2.x + nx * offsetDist, y: p2.y + ny * offsetDist };
    const textPos = { x: (ext1End.x + ext2End.x) / 2 + nx * 1.5, y: (ext1End.y + ext2End.y) / 2 + ny * 1.5 };
    return {
      ext1Start: { x: p1.x, y: p1.y }, ext1End,
      ext2Start: { x: p2.x, y: p2.y }, ext2End,
      dimStart: ext1End, dimEnd: ext2End, textPos,
    };
  };

  const handleMouseDown = useCallback((e) => {
    if (!containerRef.current) return;
    const { x, y } = getRelCoords(e);
    if (activeTool === 'highlight') {
      setPainting(true);
      setCurrentStroke([{ x, y }]);
      return;
    }
  }, [activeTool]);

  const handleMouseMove = useCallback((e) => {
    if (painting && activeTool === 'highlight') {
      const { x, y } = getRelCoords(e);
      setCurrentStroke(prev => [...prev, { x, y }]);
      return;
    }
    if (rulerStateRef.current === 'dragging_dimension' && containerRef.current) {
      const { x, y } = getRelCoords(e);
      dimensionDragPointRef.current = { x, y };
      setDimensionDragPoint({ x, y });
      return;
    }
    if (dragging && dragStartRef.current && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const dx = ((e.clientX - dragStartRef.current.mouseX) / rect.width) * 100;
      const dy = ((e.clientY - dragStartRef.current.mouseY) / rect.height) * 100;
      // Only count as a real drag if moved more than a tiny amount
      if (Math.abs(dx) > 0.2 || Math.abs(dy) > 0.2) {
        didDragRef.current = true;
      }
      if (dragging.type === 'annotation-text') {
        onUpdateAnnotation(dragging.id, { textOffsetX: dragStartRef.current.origData.offsetX + dx, textOffsetY: dragStartRef.current.origData.offsetY + dy });
      } else if (dragging.type === 'annotation-start') {
        onUpdateAnnotation(dragging.id, { x1: dragStartRef.current.origData.x1 + dx, y1: dragStartRef.current.origData.y1 + dy });
      } else if (dragging.type === 'annotation-end') {
        onUpdateAnnotation(dragging.id, { x2: dragStartRef.current.origData.x2 + dx, y2: dragStartRef.current.origData.y2 + dy });
      } else if (dragging.type === 'textbox') {
        onUpdateTextBox(dragging.id, { x: dragStartRef.current.origData.x + dx, y: dragStartRef.current.origData.y + dy });
      }
    }
  }, [painting, activeTool, dragging, onUpdateAnnotation, onUpdateTextBox]);

  const handleMouseUp = useCallback(() => {
    if (painting && currentStroke.length >= 2) {
      onAddHighlight({ id: crypto.randomUUID(), points: currentStroke, color: highlightColor, opacity: highlightOpacity, strokeWidth: highlightStrokeWidth });
    }
    setPainting(false);
    setCurrentStroke([]);
    if (rulerStateRef.current === 'dragging_dimension') {
      const geom = computeDimensionGeometry(firstPointRef.current, secondPointRef.current, dimensionDragPointRef.current);
      setFrozenGeometry(geom);
      setRulerStateBoth('pending_value');
      setShowValueInput(true);
      return;
    }
    setDragging(null);
    dragStartRef.current = null;
  }, [painting, currentStroke, highlightColor, highlightOpacity, highlightStrokeWidth, onAddHighlight]);

  const handleClick = useCallback((e) => {
    // If we just finished dragging, ignore this click
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    if (rulerStateRef.current === 'dragging_dimension' || rulerStateRef.current === 'pending_value') return;
    if (!containerRef.current) return;
    const { x, y } = getRelCoords(e);

    if (activeTool === 'ruler') {
      if (rulerStateRef.current === 'idle') {
        firstPointRef.current = { x, y };
        setFirstPoint({ x, y });
        setRulerStateBoth('placed_first');
      } else if (rulerStateRef.current === 'placed_first') {
        secondPointRef.current = { x, y };
        setSecondPoint({ x, y });
        dimensionDragPointRef.current = { x, y };
        setDimensionDragPoint({ x, y });
        setRulerStateBoth('dragging_dimension');
      }
      return;
    }
    if (activeTool === 'text') {
      setTextBoxPoint({ x, y });
      setShowTextBoxInput(true);
      setTextBoxValue('');
      return;
    }
    if (activeTool === 'delete') {
      for (const ann of image.annotations) {
        const midX = (ann.x1 + ann.x2) / 2;
        const midY = (ann.y1 + ann.y2) / 2;
        if (Math.abs(x - midX) < 5 && Math.abs(y - midY) < 5) { onRemoveAnnotation(ann.id); return; }
      }
      for (const tb of image.textBoxes || []) {
        if (Math.abs(x - tb.x) < 5 && Math.abs(y - tb.y) < 5) { onRemoveTextBox(tb.id); return; }
      }
      for (const hl of image.highlights || []) {
        for (const pt of hl.points) {
          if (Math.abs(x - pt.x) < 3 && Math.abs(y - pt.y) < 3) { onRemoveHighlight(hl.id); return; }
        }
      }
      return;
    }
    if (activeTool === 'select') {
      for (const ann of image.annotations) {
        const textX = (ann.textOffsetX !== undefined) ? (ann.x1 + ann.x2) / 2 + ann.textOffsetX : (ann.x1 + ann.x2) / 2;
        const textY = (ann.textOffsetY !== undefined) ? (ann.y1 + ann.y2) / 2 + ann.textOffsetY : (ann.y1 + ann.y2) / 2 - 1.5;
        if (Math.abs(x - textX) < 6 && Math.abs(y - textY) < 4) { onSelect(ann.id, 'annotation'); return; }
        if (Math.abs(x - ann.x1) < 3 && Math.abs(y - ann.y1) < 3) { onSelect(ann.id, 'annotation'); return; }
        if (Math.abs(x - ann.x2) < 3 && Math.abs(y - ann.y2) < 3) { onSelect(ann.id, 'annotation'); return; }
      }
      for (const tb of image.textBoxes || []) {
        if (Math.abs(x - tb.x) < 6 && Math.abs(y - tb.y) < 4) { onSelect(tb.id, 'textbox'); return; }
      }
      for (const hl of image.highlights || []) {
        for (const pt of hl.points) {
          if (Math.abs(x - pt.x) < 4 && Math.abs(y - pt.y) < 4) { onSelect(hl.id, 'highlight'); return; }
        }
      }
      onSelect(null, null);
    }
  }, [activeTool, rulerState, image, onSelect, onRemoveAnnotation, onRemoveTextBox, onRemoveHighlight]);

  const confirmAnnotation = () => {
    if (!pendingValue.trim()) return;
    const geom = frozenGeometry;
    const midX = (firstPoint.x + secondPoint.x) / 2;
    const midY = (firstPoint.y + secondPoint.y) / 2;
    onAddAnnotation({
      id: crypto.randomUUID(), x1: firstPoint.x, y1: firstPoint.y, x2: secondPoint.x, y2: secondPoint.y,
      value: pendingValue.trim(),
      textOffsetX: geom ? geom.textPos.x - midX : 0,
      textOffsetY: geom ? geom.textPos.y - midY : -3,
      style: {
        lineColor: activeToolStyle.strokeColor,
        lineWidth: activeToolStyle.lineThickness,
        fontSize: activeToolStyle.fontSize,
        textColor: activeToolStyle.strokeColor,
      },
      dimensionGeometry: geom || undefined,
    });
    setPendingValue('');
    setShowValueInput(false);
    setFrozenGeometry(null);
    setRulerStateBoth('idle');
  };

  const confirmTextBox = () => {
    if (!textBoxValue.trim()) return;
    onAddTextBox({
      id: crypto.randomUUID(), x: textBoxPoint.x, y: textBoxPoint.y, value: textBoxValue.trim(),
      style: { bold: activeToolStyle.bold, fontSize: activeToolStyle.fontSize, color: activeToolStyle.strokeColor },
    });
    setTextBoxValue('');
    setShowTextBoxInput(false);
  };

  const startDrag = (type, id, e, origData) => {
    e.stopPropagation();
    e.preventDefault();
    didDragRef.current = false; // Reset — will be set true only if mouse actually moves
    setDragging({ type, id });
    dragStartRef.current = { mouseX: e.clientX, mouseY: e.clientY, origData };
  };

  const pointsToPath = (points) => {
    if (points.length < 2) return '';
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  };

  const dimPreview = rulerState === 'dragging_dimension'
    ? computeDimensionGeometry(firstPoint, secondPoint, dimensionDragPoint)
    : rulerState === 'pending_value' ? frozenGeometry : null;

  // HTML Line helper — renders a line as a CSS-transformed div (guaranteed visible)
  const HtmlAnnotationLine = ({ x1: lx1, y1: ly1, x2: lx2, y2: ly2, color, lineWidth = 2, dashed = false, opacity: op = 1 }) => {
    const dx = lx2 - lx1, dy = ly2 - ly1;
    const length = Math.sqrt(dx * dx + dy * dy);
    if (length < 0.1) return null;
    const angle = Math.atan2(dy, dx) * (180 / Math.PI);
    return (
      <div style={{
        position: 'absolute', left: `${lx1}%`, top: `${ly1}%`, width: `${length}%`, height: 0,
        borderTop: `${lineWidth}px ${dashed ? 'dashed' : 'solid'} ${color}`,
        transformOrigin: '0 0', transform: `rotate(${angle}deg)`,
        opacity: op, pointerEvents: 'none', zIndex: 3,
      }} />
    );
  };

  // Render dimension annotation lines as HTML divs
  const renderDimensionLines = (ann, style) => {
    const dg = ann.dimensionGeometry;
    const textX = (ann.textOffsetX !== undefined) ? (ann.x1 + ann.x2) / 2 + ann.textOffsetX : (ann.x1 + ann.x2) / 2;
    const textY = (ann.textOffsetY !== undefined) ? (ann.y1 + ann.y2) / 2 + ann.textOffsetY : (ann.y1 + ann.y2) / 2 - 1.5;
    const lc = style.lineColor || '#000';
    const lines = [];
    if (dg) {
      lines.push(<HtmlAnnotationLine key={`ext1-${ann.id}`} x1={dg.ext1Start.x} y1={dg.ext1Start.y} x2={dg.ext1End.x} y2={dg.ext1End.y} color={lc} lineWidth={1.5} dashed opacity={0.85} />);
      lines.push(<HtmlAnnotationLine key={`ext2-${ann.id}`} x1={dg.ext2Start.x} y1={dg.ext2Start.y} x2={dg.ext2End.x} y2={dg.ext2End.y} color={lc} lineWidth={1.5} dashed opacity={0.85} />);
      lines.push(<HtmlAnnotationLine key={`dim-${ann.id}`} x1={dg.dimStart.x} y1={dg.dimStart.y} x2={dg.dimEnd.x} y2={dg.dimEnd.y} color={lc} lineWidth={2} />);
      const dx = dg.dimEnd.x - dg.dimStart.x, dy = dg.dimEnd.y - dg.dimStart.y, len = Math.sqrt(dx*dx + dy*dy);
      if (len > 0.5) { const nx = -dy/len*2.5, ny = dx/len*2.5;
        lines.push(<HtmlAnnotationLine key={`t1-${ann.id}`} x1={dg.dimStart.x-nx} y1={dg.dimStart.y-ny} x2={dg.dimStart.x+nx} y2={dg.dimStart.y+ny} color={lc} lineWidth={2} />);
        lines.push(<HtmlAnnotationLine key={`t2-${ann.id}`} x1={dg.dimEnd.x-nx} y1={dg.dimEnd.y-ny} x2={dg.dimEnd.x+nx} y2={dg.dimEnd.y+ny} color={lc} lineWidth={2} />);
      }
      const refX = (dg.dimStart.x + dg.dimEnd.x) / 2, refY = (dg.dimStart.y + dg.dimEnd.y) / 2;
      if (Math.abs(textX - refX) > 1 || Math.abs(textY - refY) > 1)
        lines.push(<HtmlAnnotationLine key={`leader-${ann.id}`} x1={refX} y1={refY} x2={textX} y2={textY} color={lc} lineWidth={1} dashed opacity={0.75} />);
    } else {
      lines.push(<HtmlAnnotationLine key={`line-${ann.id}`} x1={ann.x1} y1={ann.y1} x2={ann.x2} y2={ann.y2} color={lc} lineWidth={2} />);
      const dx = ann.x2-ann.x1, dy = ann.y2-ann.y1, len = Math.sqrt(dx*dx + dy*dy);
      if (len > 0.5) { const nx = -dy/len*2.5, ny = dx/len*2.5;
        lines.push(<HtmlAnnotationLine key={`t1-${ann.id}`} x1={ann.x1-nx} y1={ann.y1-ny} x2={ann.x1+nx} y2={ann.y1+ny} color={lc} lineWidth={2} />);
        lines.push(<HtmlAnnotationLine key={`t2-${ann.id}`} x1={ann.x2-nx} y1={ann.y2-ny} x2={ann.x2+nx} y2={ann.y2+ny} color={lc} lineWidth={2} />);
      }
      const refX = (ann.x1+ann.x2)/2, refY = (ann.y1+ann.y2)/2;
      if (Math.abs(textX - refX) > 1 || Math.abs(textY - refY) > 1)
        lines.push(<HtmlAnnotationLine key={`leader-${ann.id}`} x1={refX} y1={refY} x2={textX} y2={textY} color={lc} lineWidth={1} dashed opacity={0.75} />);
    }
    return <div key={`html-lines-${ann.id}`}>{lines}</div>;
  };

  // Render dimension preview while dragging — as HTML divs
  const renderDimPreviewLines = () => {
    if (!dimPreview) return null;
    const lc = activeToolStyle.strokeColor || '#000';
    const lines = [];
    lines.push(<HtmlAnnotationLine key="dp-ext1" x1={dimPreview.ext1Start.x} y1={dimPreview.ext1Start.y} x2={dimPreview.ext1End.x} y2={dimPreview.ext1End.y} color={lc} lineWidth={1} dashed opacity={0.6} />);
    lines.push(<HtmlAnnotationLine key="dp-ext2" x1={dimPreview.ext2Start.x} y1={dimPreview.ext2Start.y} x2={dimPreview.ext2End.x} y2={dimPreview.ext2End.y} color={lc} lineWidth={1} dashed opacity={0.6} />);
    lines.push(<HtmlAnnotationLine key="dp-dim" x1={dimPreview.dimStart.x} y1={dimPreview.dimStart.y} x2={dimPreview.dimEnd.x} y2={dimPreview.dimEnd.y} color={lc} lineWidth={2} />);
    const dx = dimPreview.dimEnd.x-dimPreview.dimStart.x, dy = dimPreview.dimEnd.y-dimPreview.dimStart.y, len = Math.sqrt(dx*dx+dy*dy);
    if (len > 0.5) { const nx = -dy/len*2, ny = dx/len*2;
      lines.push(<HtmlAnnotationLine key="dp-t1" x1={dimPreview.dimStart.x-nx} y1={dimPreview.dimStart.y-ny} x2={dimPreview.dimStart.x+nx} y2={dimPreview.dimStart.y+ny} color={lc} lineWidth={1.5} />);
      lines.push(<HtmlAnnotationLine key="dp-t2" x1={dimPreview.dimEnd.x-nx} y1={dimPreview.dimEnd.y-ny} x2={dimPreview.dimEnd.x+nx} y2={dimPreview.dimEnd.y+ny} color={lc} lineWidth={1.5} />);
    }
    return <>{lines}</>;
  };

  return (
    <div className="relative" data-testid="annotation-canvas">
      <div ref={containerRef} className="relative aspect-square" style={{ cursor: getCursor() }}
        onClick={handleClick} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}
      >
        <img src={image.url} alt={image.label} className="w-full h-full object-contain bg-white" draggable={false} />
        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ zIndex: 2 }}>
          {/* Highlights — freeform strokes need SVG <path> */}
          {(image.highlights || []).map(hl => (
            <path key={hl.id} d={pointsToPath(hl.points)} fill="none" stroke={hl.color} strokeWidth={hl.strokeWidth} strokeLinecap="round" strokeLinejoin="round" opacity={hl.opacity} className={selectedId === hl.id ? 'animate-pulse' : ''} />
          ))}
          {painting && currentStroke.length >= 2 && (
            <path d={pointsToPath(currentStroke)} fill="none" stroke={highlightColor} strokeWidth={highlightStrokeWidth} strokeLinecap="round" strokeLinejoin="round" opacity={highlightOpacity} />
          )}
          {/* Selection outlines */}
          {image.annotations.map(ann => selectedId === ann.id ? (
            <rect key={`sel-${ann.id}`} x={Math.min(ann.x1, ann.x2) - 1} y={Math.min(ann.y1, ann.y2) - 1} width={Math.abs(ann.x2 - ann.x1) + 2} height={Math.abs(ann.y2 - ann.y1) + 2} fill="none" stroke="#3b82f6" strokeWidth="0.3" strokeDasharray="1,1" opacity="0.6" />
          ) : null)}
          {(image.textBoxes || []).map(tb => selectedId === tb.id ? (
            <rect key={`sel-tb-${tb.id}`} x={tb.x - 1} y={tb.y - 3} width="15" height="5" fill="none" stroke="#3b82f6" strokeWidth="0.3" strokeDasharray="1,1" opacity="0.6" />
          ) : null)}
          {/* Ruler: first point indicator */}
          {activeTool === 'ruler' && rulerState === 'placed_first' && (
            <circle cx={firstPoint.x} cy={firstPoint.y} r="1.5" fill={activeToolStyle.strokeColor} opacity="0.9" stroke="white" strokeWidth="0.3" />
          )}
          {/* Ruler preview line removed — dimension preview handled by HTML divs below */}
        </svg>

        {/* Annotation dimension lines — HTML divs for guaranteed visibility */}
        {image.annotations.map(ann => {
          const style = ann.style || DEFAULT_ANNOTATION_STYLE;
          return renderDimensionLines(ann, style);
        })}

        {/* Dimension preview while dragging — HTML divs */}
        {renderDimPreviewLines()}

        {/* HTML text overlays for annotations - rendered outside SVG for reliable text rendering */}
        {image.annotations.map(ann => {
          const style = ann.style || DEFAULT_ANNOTATION_STYLE;
          const textX = (ann.textOffsetX !== undefined) ? (ann.x1 + ann.x2) / 2 + ann.textOffsetX : (ann.x1 + ann.x2) / 2;
          const textY = (ann.textOffsetY !== undefined) ? (ann.y1 + ann.y2) / 2 + ann.textOffsetY : (ann.y1 + ann.y2) / 2 - 1.5;
          return (
            <div key={`text-${ann.id}`} className="absolute pointer-events-none z-10"
              style={{ left: `${textX}%`, top: `${textY}%`, transform: 'translate(-50%, -50%)' }}>
              <span className="px-1.5 py-0.5 rounded text-nowrap inline-block shadow-sm"
                style={{
                  fontSize: `clamp(11px, ${style.fontSize * 0.28}rem, 22px)`,
                  fontWeight: 'bold',
                  fontFamily: 'Arial, sans-serif',
                  color: style.textColor || '#000',
                  backgroundColor: 'rgba(255,255,255,0.92)',
                  border: '1px solid rgba(0,0,0,0.12)',
                  lineHeight: 1.2,
                }}
                data-testid={`annotation-label-${ann.id}`}
              >{ann.value}</span>
            </div>
          );
        })}

        {/* HTML text overlays for text boxes */}
        {(image.textBoxes || []).map(tb => {
          const style = tb.style || DEFAULT_TEXTBOX_STYLE;
          return (
            <div key={`text-tb-${tb.id}`} className="absolute pointer-events-none z-10"
              style={{ left: `${tb.x}%`, top: `${tb.y}%`, transform: 'translate(0, -50%)' }}>
              <span className="text-nowrap inline-block"
                style={{
                  fontSize: `clamp(11px, ${(style.fontSize || 4.5) * 0.28}rem, 22px)`,
                  fontWeight: style.bold ? 'bold' : 'normal',
                  fontFamily: 'Arial, sans-serif',
                  color: style.color || '#000',
                  lineHeight: 1.2,
                }}
                data-testid={`textbox-label-${tb.id}`}
              >{tb.value}</span>
            </div>
          );
        })}

        {/* HTML preview "?" text during dimension placement */}
        {dimPreview && (
          <div className="absolute pointer-events-none z-10"
            style={{ left: `${dimPreview.textPos.x}%`, top: `${dimPreview.textPos.y}%`, transform: 'translate(-50%, -50%)' }}>
            <span className="px-1.5 py-0.5 rounded text-nowrap inline-block"
              style={{
                fontSize: `clamp(11px, ${activeToolStyle.fontSize * 0.28}rem, 22px)`,
                fontWeight: 'bold',
                fontFamily: 'Arial, sans-serif',
                color: activeToolStyle.strokeColor,
                backgroundColor: 'rgba(255,255,255,0.75)',
                lineHeight: 1.2,
              }}
            >?</span>
          </div>
        )}

        {/* Drag handles for Select tool — rendered OUTSIDE the SVG as DOM elements */}
        {activeTool === 'select' && image.annotations.map(ann => {
          const textX = (ann.textOffsetX !== undefined) ? (ann.x1 + ann.x2) / 2 + ann.textOffsetX : (ann.x1 + ann.x2) / 2;
          const textY = (ann.textOffsetY !== undefined) ? (ann.y1 + ann.y2) / 2 + ann.textOffsetY : (ann.y1 + ann.y2) / 2 - 1.5;
          return (
            <div key={`handles-${ann.id}`}>
              {/* Start-point handle */}
              <div
                className="absolute w-3.5 h-3.5 rounded-full bg-primary border-2 border-white shadow-md cursor-move z-20 hover:scale-125 transition-transform"
                style={{ left: `${ann.x1}%`, top: `${ann.y1}%`, transform: 'translate(-50%, -50%)' }}
                onMouseDown={e => startDrag('annotation-start', ann.id, e, { x1: ann.x1, y1: ann.y1 })}
                data-testid={`handle-start-${ann.id}`}
              />
              {/* End-point handle */}
              <div
                className="absolute w-3.5 h-3.5 rounded-full bg-primary border-2 border-white shadow-md cursor-move z-20 hover:scale-125 transition-transform"
                style={{ left: `${ann.x2}%`, top: `${ann.y2}%`, transform: 'translate(-50%, -50%)' }}
                onMouseDown={e => startDrag('annotation-end', ann.id, e, { x2: ann.x2, y2: ann.y2 })}
                data-testid={`handle-end-${ann.id}`}
              />
              {/* Text label handle */}
              <div
                className="absolute cursor-move z-20 rounded border-2 border-primary/40 bg-primary/10 hover:bg-primary/20 transition-colors"
                style={{ left: `${textX - 5}%`, top: `${textY - 2.5}%`, width: '10%', height: '5%' }}
                onMouseDown={e => startDrag('annotation-text', ann.id, e, { offsetX: ann.textOffsetX || 0, offsetY: ann.textOffsetY || 0 })}
                title="Drag to move label"
              />
            </div>
          );
        })}
        {activeTool === 'select' && (image.textBoxes || []).map(tb => (
          <div
            key={`handle-tb-${tb.id}`}
            className="absolute cursor-move z-20 rounded border-2 border-primary/40 bg-primary/10 hover:bg-primary/20 transition-colors"
            style={{ left: `${tb.x - 1}%`, top: `${tb.y - 3}%`, width: '14%', height: '6%' }}
            onMouseDown={e => startDrag('textbox', tb.id, e, { x: tb.x, y: tb.y })}
            title="Drag to move text"
          />
        ))}

        {/* Tool hint banners */}
        {activeTool === 'ruler' && rulerState === 'idle' && !showValueInput && (
          <div className="absolute bottom-0 left-0 right-0 bg-primary text-primary-foreground text-xs text-center py-1.5">Click the start point</div>
        )}
        {activeTool === 'ruler' && rulerState === 'placed_first' && (
          <div className="absolute bottom-0 left-0 right-0 bg-primary text-primary-foreground text-xs text-center py-1.5">Click the end point</div>
        )}
        {rulerState === 'dragging_dimension' && (
          <div className="absolute bottom-0 left-0 right-0 bg-primary text-primary-foreground text-xs text-center py-1.5">Drag to position dimension line, then release</div>
        )}
        {activeTool === 'text' && !showTextBoxInput && (
          <div className="absolute bottom-0 left-0 right-0 bg-accent text-accent-foreground text-xs text-center py-1.5">Click where you want to place text</div>
        )}
        {activeTool === 'highlight' && (
          <div className="absolute bottom-0 left-0 right-0 bg-accent text-accent-foreground text-xs text-center py-1.5">Paint to highlight</div>
        )}
        {activeTool === 'delete' && (
          <div className="absolute bottom-0 left-0 right-0 bg-destructive text-destructive-foreground text-xs text-center py-1.5">Click an annotation to delete it</div>
        )}
      </div>

      {/* Value input for ruler annotations */}
      {showValueInput && (
        <div className="flex items-center gap-1.5 p-2 bg-card border-t border-border">
          <span className="text-[10px] text-muted-foreground whitespace-nowrap">Dim:</span>
          <input autoFocus value={pendingValue} onChange={e => setPendingValue(e.target.value)} placeholder="e.g. 5.80mm"
            className="flex-1 h-6 text-xs px-2 rounded border border-input bg-background text-foreground"
            onKeyDown={e => { if (e.key === 'Enter') confirmAnnotation(); if (e.key === 'Escape') { setShowValueInput(false); setFrozenGeometry(null); setRulerStateBoth('idle'); } }} />
          <button onClick={confirmAnnotation} className="h-6 px-2 text-[10px] bg-primary text-primary-foreground rounded font-medium">Add</button>
          <button onClick={() => { setShowValueInput(false); setFrozenGeometry(null); setRulerStateBoth('idle'); }} className="h-6 px-2 text-[10px] text-muted-foreground">Cancel</button>
        </div>
      )}

      {/* Text input for text boxes */}
      {showTextBoxInput && (
        <div className="flex items-center gap-1.5 p-2 bg-card border-t border-border">
          <span className="text-[10px] text-muted-foreground whitespace-nowrap">Text:</span>
          <input autoFocus value={textBoxValue} onChange={e => setTextBoxValue(e.target.value)} placeholder="e.g. Finger Size: 7"
            className="flex-1 h-6 text-xs px-2 rounded border border-input bg-background text-foreground"
            onKeyDown={e => { if (e.key === 'Enter') confirmTextBox(); if (e.key === 'Escape') setShowTextBoxInput(false); }} />
          <button onClick={confirmTextBox} className="h-6 px-2 text-[10px] bg-primary text-primary-foreground rounded font-medium">Add</button>
          <button onClick={() => setShowTextBoxInput(false)} className="h-6 px-2 text-[10px] text-muted-foreground">Cancel</button>
        </div>
      )}
    </div>
  );
}
